/*
  APO Integration Layer
  =====================

  The Manifold APO (Advanced Product Options) app injects product option UI
  into .mw-apo-container asynchronously. Because we can't predict when that
  DOM appears, we use a single shared MutationObserver (ApoObserver) that any
  feature can subscribe to.

  Additionally, mws_apo_helper.liquid dispatches two CustomEvents on `window`
  that mirror APO's internal lifecycle:

    - "apo:event"    → detail.event contains the APO event name (e.g. "init::finish")
    - "apo:callback" → detail.event contains the callback name
                        (e.g. "applyAttributeStateToHtml::price")

  Consumers in this file and apo-param-selector.js listen to these events
  to coordinate with APO's lifecycle rather than using arbitrary timeouts.

  ApoObserver — Global MutationObserver singleton for .mw-apo-container
    ApoObserver.register(fn)   → fn(container) fires on discovery + every mutation
    ApoObserver.getContainer() → returns the container element or null

  <apo-popup-opener> — Custom element that appends a popup modal-opener inside
    a matching APO option label. Uses ApoObserver to retry until the option
    appears. Configured via the "option-name" attribute; opener HTML goes in a
    child <template>.

  ApoParamSelector (in apo-param-selector.js) — Reads URL search params and
    preselects matching APO options after init::finish. See that file for details.

  Logging:
    ApoObserver.debug = true   → enables [ApoObserver] console logs
*/

const ApoObserver = (() => {
  let debug = false;
  const callbacks = [];
  let container = null;
  let observer = null;
  let initialized = false;

  const log = (...args) => { if (debug) console.log('[ApoObserver]', ...args); };

  const notify = () => {
    for (const cb of callbacks) {
      try {
        cb(container);
      } catch (e) {
        console.error('[ApoObserver] callback error:', e);
      }
    }
  };

  const startObserving = (el) => {
    container = el;
    observer = new MutationObserver(notify);
    observer.observe(container, { childList: true, subtree: true });
    log('observing .mw-apo-container');
    notify();
  };

  const ensureContainer = () => {
    const current = document.querySelector('.mw-apo-container');
    if (current && current !== container) {
      if (observer) observer.disconnect();
      startObserving(current);
    } else if (current) {
      notify();
    }
  };

  const init = () => {
    if (initialized) return;
    initialized = true;

    const existing = document.querySelector('.mw-apo-container');
    if (existing) {
      startObserving(existing);
    } else {
      const waitObserver = new MutationObserver(() => {
        const el = document.querySelector('.mw-apo-container');
        if (el) {
          waitObserver.disconnect();
          startObserving(el);
        }
      });
      waitObserver.observe(document.body, { childList: true, subtree: true });
      log('waiting for .mw-apo-container');
    }

    window.addEventListener('apo:event', ensureContainer);
    window.addEventListener('apo:callback', ensureContainer);
  };

  return {
    get debug() { return debug; },
    set debug(v) { debug = v; },

    register(fn) {
      callbacks.push(fn);
      if (!initialized) init();
      if (container) {
        try { fn(container); } catch (e) { console.error('[ApoObserver] callback error:', e); }
      }
    },

    unregister(fn) {
      const idx = callbacks.indexOf(fn);
      if (idx !== -1) callbacks.splice(idx, 1);
    },

    getContainer() {
      return container;
    },
  };
})();

class ApoPopupOpener extends HTMLElement {
  connectedCallback() {
    this.optionName = this.getAttribute('option-name');
    this.templateEl = this.querySelector('template');

    if (!this.optionName || !this.templateEl) return;

    if (!this._boundTryAppend) {
      this._boundTryAppend = () => this.tryAppend();
    }

    ApoObserver.register(this._boundTryAppend);
  }

  disconnectedCallback() {
    if (this._boundTryAppend) {
      ApoObserver.unregister(this._boundTryAppend);
    }
  }

  tryAppend() {
    const labels = document.querySelectorAll('.mw-apo-container .mw-option .mw-label');
    const needles = this.optionName
      .split(',')
      .map((n) => n.trim().toLowerCase())
      .filter(Boolean);
    if (!needles.length) return;
    for (const label of labels) {
      const text = label.textContent.trim().toLowerCase();
      if (!needles.some((n) => text.includes(n))) continue;
      if (label.querySelector('.product-popup-modal__opener')) continue;
      label.appendChild(this.templateEl.content.cloneNode(true));
    }
  }
}

customElements.define('apo-popup-opener', ApoPopupOpener);

